import React from 'react'
import { Row, Col, Button } from 'reactstrap'
import { DataGrid } from '@mui/x-data-grid'
import { Chip } from '@mui/material'
import Alert from '@mui/material/Alert'
import { withFirebase } from '../../components/Fb'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import CopyButton from '../../components/Buttons/CopyButton'
import ContractTemplateEditor from '../../components/Details/ContractTemplateEditor'
import ContractTemplateDeleteDialog from '../../components/Dialogs/ContractTemplateDeleteDialog'
import {
  SUBJECT_OPTIONS,
  TEMPLATE_STATES,
  VALIDITY_OPTIONS,
  labelFor,
  templatesFromRecurrentTerms,
} from '../../components/utils/contracts'

// Firestore errors carry a `code` like 'permission-denied' that says far more than the
// message alone, and a bare error object renders as [object Object].
const describeError = (error) => {
  if (!error) return 'tuntematon virhe'
  const code = error.code ? '[' + error.code + '] ' : ''
  return code + (error.message || String(error))
}

/**
 * Contract templates - what a contract is made of.
 *
 * Replaces the two contract types that were hardcoded in ReservationModal's dropdown and
 * the single config/recurrentterms blob keyed [venue][type][lang]. A venue defines as many
 * variants as it needs, and `contracttype` is demoted to a label on the template.
 */
class ContractTemplatesContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      templates: [],
      venues: [],
      editorOpen: false,
      selectedTemplate: null,
      deleteTarget: null,
      working: false,
      seedResult: null,
    }
  }

  componentDidMount() {
    this.updateData()
    this.loadVenues()
  }

  updateData = async () => {
    try {
      const templates = await this.props.firebase.getContractTemplates()
      this.setState({ templates, loading: false })
    } catch (error) {
      console.error('Contract templates failed to load', error)
      this.setState({ templates: [], loading: false })
    }
  }

  loadVenues = async () => {
    try {
      const snapshot = await this.props.firebase.getVenues()
      if (snapshot && !snapshot.empty) {
        // The terms blob keys venues by NAME, so the template scoping does too.
        const venues = snapshot.docs.map((doc) => doc.data().name || doc.id).filter(Boolean)
        this.setState({ venues })
      }
    } catch (error) {
      console.error('Venues failed to load', error)
    }
  }

  addNewTemplate = () => this.setState({ editorOpen: true, selectedTemplate: null })

  editTemplate = (template) => this.setState({ editorOpen: true, selectedTemplate: template })

  copyTemplate = (template) => {
    const copy = { ...template, name: (template.name || '') + ' (kopio)' }
    delete copy.id
    delete copy.created
    delete copy.updated
    delete copy.creatorId
    delete copy.updaterId
    this.setState({ editorOpen: true, selectedTemplate: copy })
  }

  closeEditor = () => this.setState({ editorOpen: false, selectedTemplate: null })

  handleSave = async (template) => {
    if (template.id) {
      await this.props.firebase.updateContractTemplate(template)
    } else {
      await this.props.firebase.createContractTemplate(template)
    }
    this.setState({ editorOpen: false, selectedTemplate: null, loading: true }, this.updateData)
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    if (!target) return
    this.setState({ working: true })
    await this.props.firebase.deleteContractTemplate(target.id)
    this.setState({ working: false, deleteTarget: null, loading: true }, this.updateData)
  }

  // Imports the old config/recurrentterms wording so nobody has to retype live contract
  // terms by hand. The config doc is left untouched - it is the rollback path.
  seedFromTerms = async () => {
    this.setState({ working: true, seedResult: null })

    // Read and write are reported separately. They fail for completely different reasons -
    // a missing config doc versus a rejected write to a brand new collection - and one
    // shared "it failed" message sends you looking in the wrong place.
    let terms = null
    try {
      const doc = await this.props.firebase.getRecurrentTerms().get()
      terms = doc.exists ? doc.data() : null
    } catch (error) {
      console.error('Reading config/recurrentterms failed', error)
      this.setState({
        working: false,
        seedResult: { severity: 'error', message: 'Vanhojen ehtojen luku epäonnistui: ' + describeError(error) },
      })
      return
    }

    try {
      const drafts = templatesFromRecurrentTerms(terms)
      if (drafts.length === 0) {
        this.setState({
          working: false,
          seedResult: {
            severity: 'info',
            message: terms
              ? 'Vanhoista ehdoista ei löytynyt tekstiä tuotavaksi. Sopimusehdot ovat tyhjät.'
              : 'Vanhoja sopimusehtoja ei löytynyt lainkaan (config/recurrentterms puuttuu).',
          },
        })
        return
      }
      // Importing twice would silently duplicate every template, so anything already
      // imported is skipped by its legacy type + venue scope.
      const existing = this.state.templates
      const isAlreadyImported = (draft) =>
        existing.some(
          (t) =>
            t.legacytype === draft.legacytype &&
            JSON.stringify((t.venues || []).slice().sort()) === JSON.stringify(draft.venues.slice().sort())
        )
      const fresh = drafts.filter((draft) => !isAlreadyImported(draft))
      if (fresh.length === 0) {
        this.setState({ working: false, seedResult: { severity: 'info', message: 'Kaikki vanhat ehdot on jo tuotu.' } })
        return
      }
      // Written one at a time so a failure halfway through still says how far it got -
      // the ones already created are real and re-importing skips them.
      let created = 0
      for (let i = 0; i < fresh.length; i += 1) {
        try {
          await this.props.firebase.createContractTemplate(fresh[i])
          created += 1
        } catch (error) {
          console.error('Creating contract template failed', fresh[i].name, error)
          this.setState({
            working: false,
            loading: true,
            seedResult: {
              severity: 'error',
              message:
                'Tuonti keskeytyi mallin "' + fresh[i].name + '" kohdalla: ' + describeError(error) +
                (created > 0 ? ' Ehdittiin tuoda ' + created + '.' : ''),
            },
          }, this.updateData)
          return
        }
      }
      this.setState(
        {
          working: false,
          loading: true,
          seedResult: { severity: 'success', message: 'Tuotiin ' + created + ' sopimusmallia. Tarkista tekstit ennen käyttöä.' },
        },
        this.updateData
      )
    } catch (error) {
      console.error('Seeding contract templates failed', error)
      this.setState({
        working: false,
        seedResult: { severity: 'error', message: 'Tuonti epäonnistui: ' + describeError(error) },
      })
    }
  }

  getColDefinitions = () => [
    { field: 'name', headerName: 'Nimi', flex: 2, minWidth: 180 },
    { field: 'contracttype', headerName: 'Sopimustyyppi', flex: 1.5, minWidth: 130 },
    {
      field: 'validity',
      headerName: 'Voimassaolo',
      flex: 1.5,
      minWidth: 140,
      valueGetter: (value, row) => labelFor(VALIDITY_OPTIONS, row.validity),
    },
    {
      field: 'subjecttype',
      headerName: 'Käytetään',
      width: 130,
      valueGetter: (value, row) => labelFor(SUBJECT_OPTIONS, row.subjecttype),
    },
    {
      field: 'venues',
      headerName: 'Toimipisteet',
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      valueGetter: (value, row) => (row.venues && row.venues.length > 0 ? row.venues.join(', ') : 'Kaikki'),
    },
    {
      field: 'state',
      headerName: 'Tila',
      width: 110,
      renderCell: (params) => {
        const value = params.row.state || 'active'
        return (
          <Chip
            size='small'
            label={labelFor(TEMPLATE_STATES, value)}
            color={value === 'active' ? 'primary' : 'default'}
            variant={value === 'active' ? 'filled' : 'outlined'}
          />
        )
      },
    },
    {
      field: 'edit',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => <EditButton disablediv onClick={() => this.editTemplate(params.row)} />,
    },
    {
      field: 'copy',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => <CopyButton onClick={() => this.copyTemplate(params.row)} />,
    },
    {
      field: 'delete',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => <DeleteButton disablediv onClick={() => this.setState({ deleteTarget: params.row })} />,
    },
  ]

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }

    if (this.state.editorOpen) {
      return (
        <div className='content'>
          <ContractTemplateEditor
            template={this.state.selectedTemplate}
            venues={this.state.venues}
            onSave={this.handleSave}
            onCancel={this.closeEditor}
          />
        </div>
      )
    }

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button size='sm' onClick={this.addNewTemplate}>
              Lisää sopimusmalli
            </Button>{' '}
            <Button size='sm' onClick={this.seedFromTerms} disabled={this.state.working}>
              {this.state.working ? 'Tuodaan...' : 'Tuo nykyiset ehdot'}
            </Button>
          </Col>
        </Row>
        {this.state.seedResult && (
          <Alert severity={this.state.seedResult.severity} sx={{ mb: 2 }} onClose={() => this.setState({ seedResult: null })}>
            {this.state.seedResult.message}
          </Alert>
        )}
        {this.state.templates.length === 0 && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Ei sopimusmalleja. Tuo nykyiset ehdot vanhasta Sopimusehdot-näkymästä tai luo malli tyhjästä —
            ilman mallia varaukselle ei voi tehdä sopimusta.
          </Alert>
        )}
        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 260px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={this.state.templates}
                  columns={this.getColDefinitions()}
                  density='compact'
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </div>
            </div>
          </Col>
        </Row>
        <ContractTemplateDeleteDialog
          open={!!this.state.deleteTarget}
          name={this.state.deleteTarget ? this.state.deleteTarget.name : ''}
          working={this.state.working}
          onCancel={() => this.setState({ deleteTarget: null })}
          onConfirm={this.confirmDelete}
        />
      </div>
    )
  }
}

export default withFirebase(ContractTemplatesContainer)
